/**
 * Normalize an internal path to the site's canonical trailing-slash form.
 *
 * This site is configured with `trailingSlash: true` + `output: 'export'`, so
 * the canonical URL for every route is `/path/` and each route is emitted as
 * `/path/index.html`. A `<Link href="/path">` (no trailing slash) does NOT match
 * the exported route table, so Next's client router declines to intercept the
 * click and the browser performs a native full-document navigation — a full page
 * reload that re-downloads every chunk and re-hydrates the whole app.
 *
 * Appending the trailing slash keeps navigations client-side (soft nav). Note
 * `router.push()` already normalizes internally; this matters specifically for
 * `<Link>`/`<a>` hrefs.
 *
 * The exceptions mirror Next's own `trailingSlash` behavior: static files (any
 * last segment containing a `.` extension) and `.well-known/` paths are left
 * untouched, as are external URLs, protocol-relative URLs, non-http schemes
 * (`mailto:`, `tel:`, …), and bare hash/query fragments.
 */
/**
 * CMS editors routinely paste an email address straight into the link field, so
 * Strapi stores `href="someone@example.com"` with no scheme. The browser then
 * treats it as a RELATIVE PATH: clicking it navigates to
 * `/current/page/someone@example.com` (a 404) instead of opening a mail client.
 *
 * This was invisible for years because the affected block (Let's Connect) was
 * fetched client-side, so no crawler or link checker ever saw the anchor. It was
 * always broken for real users. Normalising here fixes every CMS rich-text block
 * at once rather than patching one call site.
 */
export function mailtoHref(href: string): string {
  if (!href) return href
  // Already has a scheme (mailto:, http:, tel:, …), or is a path/anchor/query.
  if (/^[a-z][a-z0-9+.-]*:/i.test(href) || /^[/#?]/.test(href) || href.startsWith('//')) return href
  // Conservative shape check: exactly one @, a dot-bearing domain, no whitespace
  // or slashes. Anything else is left alone for canonicalHref to handle.
  return /^[^\s/@]+@[^\s/@]+\.[^\s/@]+$/.test(href) ? `mailto:${href}` : href
}

export function canonicalHref(href: string): string {
  if (!href) return href

  // External URLs, protocol-relative URLs, and non-http schemes (mailto:, tel:).
  if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('//')) return href

  // Bare in-page hash or query string — no pathname to normalize.
  if (href.startsWith('#') || href.startsWith('?')) return href

  // Split the pathname from any ?query / #hash so the slash lands on the path.
  const splitAt = href.search(/[?#]/)
  const path = splitAt === -1 ? href : href.slice(0, splitAt)
  const suffix = splitAt === -1 ? '' : href.slice(splitAt)

  if (!path || path.endsWith('/')) return href

  // Leave static files (with an extension) and `.well-known/` untouched.
  const lastSegment = path.slice(path.lastIndexOf('/') + 1)
  if (lastSegment.includes('.')) return href
  if (path.startsWith('/.well-known')) return href

  return `${path}/${suffix}`
}

/**
 * Routes renamed in-place after go-live, keyed old → new (no trailing slash).
 *
 * The app's own `<Link>`s were updated with the rename, but CMS rich-text bodies
 * are authored in Strapi and still carry some of the old slugs. Under
 * `output: 'export'` the old path is not emitted at all, so such an anchor is a
 * hard 404 — see {@link resolveRenamedPath}, applied to CMS anchors in
 * `parseCmsHtml`.
 *
 * This is a safety net, not the fix of record: the authoritative fix is updating
 * the link in Strapi. Entries may be dropped once the CMS is clean.
 */
export const RENAMED_ROUTES: Record<string, string> = {
  '/personal/deposits/commodity-murabahah-sa-i':
    '/personal/deposits/commodity-murabahah-savings-account-i',
  '/personal/deposits/commodity-murabahah-ca-i':
    '/personal/deposits/commodity-murabahah-current-account-i',
  '/personal/deposits/commodity-murabahah-fc-ca-i':
    '/personal/deposits/commodity-murabahah-foreign-currency-current-account-i',
  '/personal/cards/rafahia-card': '/personal/cards/rafahia-debit-card-i',
  '/personal/financing/personal-financing-td-i':
    '/personal/financing/personal-financing-against-term-deposit',
  '/personal/financing/personal-financing-i-ebs':
    '/personal/financing/personal-financing-employee-banking',
  '/personal/financing/home-financing-i-SJKP': '/personal/financing/home-financing-i-sjkp',
  '/business/deposits/commodity-murabahah-td-i':
    '/business/deposits/commodity-murabahah-term-deposit-i',
  '/business/deposits/commodity-murabahah-biz':
    '/business/deposits/commodity-murabahah-business-current-account-i',
  '/business/financing/structured-commodity': '/business/financing/structured-commodity-financing-i'
}

/**
 * Map a renamed legacy internal path to its current one, preserving any
 * query/hash. Non-legacy hrefs (and every external/non-http href) pass through
 * unchanged. Deliberately NOT folded into {@link canonicalHref}: the link
 * integrity suite classifies emitted hrefs through `canonicalHref`, and a rename
 * applied there would silently "fix" broken links in the assertion instead of in
 * the shipped HTML.
 */
export function resolveRenamedPath(href: string): string {
  if (!href || !href.startsWith('/')) return href

  const splitAt = href.search(/[?#]/)
  const path = splitAt === -1 ? href : href.slice(0, splitAt)
  const suffix = splitAt === -1 ? '' : href.slice(splitAt)

  const replacement = RENAMED_ROUTES[stripTrailingSlash(path)]
  if (!replacement) return href

  return `${replacement}${path.endsWith('/') ? '/' : ''}${suffix}`
}

/** Strip a trailing slash, preserving the root path ("/" stays "/"). */
export function stripTrailingSlash(path?: string | null): string {
  const p = path ?? ''
  return p.length > 1 ? p.replaceAll(/\/+$/g, '') : p
}

/**
 * Compare two route paths ignoring trailing slashes.
 *
 * Under `trailingSlash: true`, `usePathname()` returns the canonical
 * slash-suffixed form (e.g. "/news/"), so a naive `pathname === '/news'` is
 * always false — every active-nav / current-page check must normalize first.
 */
export function isSamePath(a?: string | null, b?: string | null): boolean {
  return stripTrailingSlash(a) === stripTrailingSlash(b)
}

/** Open a PDF in a new tab. Link/navigation behaviour, hence this module. */
export const handleOpenPdf = pdf => {
  window.open(pdf)
}
